import Vue from 'vue';
import * as types from '../actionTypes';
import { getEventData } from '../../api';
import helper from '../../helpers/helper';
import { setJourneyBannersData } from '../../helpers/branch.io';
import { setHeader } from '../../api/httpService';
import { decodeBase64 } from '../../helpers/base64';
import getQueryParams from '../../helpers/getQueryParams';
import router from '../../router';

import { actions as highlightsActions } from "./mainHighlights";
import { actions as linksActions, mutations as linksMutations } from "./mainLinks";

const initialVideoData = () => ({
    mainPlaybackData: {
        data: null,
        error: false,
        errorMessage: ''
    },
    streams: {
        data: [],
        error: false,
        errorMessage: ''
    },
    sharedLinksList: {},
    activePlaybackId: '',
    eventData: {}
});

const state = () => {
    return {
        video: initialVideoData(),
        nextEventToken: null,
        prevEventToken: null,
        mainToken: null,
        urlHashData: null
    }
};

const getters = {
    activePlayback(state) {
        return state.video.streams.data.find(item => item.id === state.video.activePlaybackId);
    },
    nextPlayback(state) {
        const list = state.video.streams.data;
        return list[list.findIndex(item => item.id === state.video.activePlaybackId) + 1];
    },
    activePlaybackSharedLinkData(state) {
        return state.video.sharedLinksList[state.video.activePlaybackId]
    },
    activePlaybackThumbnail(state, getters) {
        if (getters.activePlayback) {
            return getters.activePlayback.attributes.urls.thumbnail;
        }
    },
};

const actions = {
    ...linksActions,
    ...highlightsActions,

    [types.MAIN_FETCHING_ACTION]({ dispatch, commit }, { hash }) {
        const url = window.location.href;
        let parsedTokenData, token;

        try {
            parsedTokenData = decodeBase64(hash);
            if (parsedTokenData.token) {
                token = parsedTokenData.token;
            } else {
                token = getQueryParams(window.location.hash.substring(1)).token;
            }

            if (!parsedTokenData.id || !token) {
                throw new Error();
            }
        } catch (error) {
            /** redirect to not found page **/
            router.push('/404');
        }

        const { id, event, type, timeStamp, playerTimeStamp } = parsedTokenData;

        commit(types.SET_URL_HASH_DATA, { event, token });

        /** set single authorization header for all requests **/
        setHeader('X-Shared-Authorization', token);

        setJourneyBannersData(type, url);

        new Promise((resolve => {
            switch (type) {
                case 'clip':
                    resolve(dispatch(types.FETCH_SHARE_REQUEST, { type, id }));
                    break;
                case 'hd':
                    resolve(dispatch(types.FETCH_EVENT_REQUEST, id));
                    break;
                case 'autohighlight':
                    resolve(dispatch(types.FETCH_EVENT_HIGHLIGHT_REQUEST, id));
                    break;
                case 'player-highlight':
                    resolve(dispatch(types.FETCH_PLAYER_HIGHLIGHT_REQUEST, id));
                    break;
            }
        }))
            .then(([formattedData, additionalEvents]) => {
                return helper.getPlayerStartTimeFromTimeStamps(timeStamp, playerTimeStamp, event)
                    .then(startTime => {
                        formattedData.startTime = startTime;

                        return [formattedData, additionalEvents];
                    })
            })
            .then(([formattedData, additionalEvents]) => {
                commit(types.SET_ADDITIONAL_EVENT_TOKENS, additionalEvents);
                commit(types.MAIN_FETCH_SUCCESS, formattedData);

                dispatch(types.CHANGE_ACTIVE_PLAYBACK, formattedData.id);
                dispatch(types.CREATE_SHARED_LINK_ACTION, formattedData);

                switch (type) {
                    case 'hd':
                        dispatch(types.FETCH_PLAYER_HIGHLIGHTS_REQUEST, event);
                        break;
                    case 'clip':
                    case 'autohighlight':
                    case 'player-highlight':
                        dispatch(types.FETCH_EVENT_REQUEST, event);
                        dispatch(types.FETCH_PLAYER_HIGHLIGHTS_REQUEST, event);
                }
            })
            .catch(error => {
                commit(types.MAIN_FETCH_FAILURE, error.message);

                /** redirect to not found page **/
                router.push('/404');
            })
    },

    [types.SWITCH_TO_NEARBY_EVENT]({ commit, dispatch, state }, direction) {
        const hash = state[`${direction}EventToken`];

        router.push(`/share/${hash}/hd`);

        commit(types.INIT_VIDEO_DATA);
        commit(types.SET_ADDITIONAL_EVENT_TOKENS, { nextEventToken: null, prevEventToken: null });

        dispatch(types.MAIN_FETCHING_ACTION, { hash, type: 'hd' });
    },

    /** action for fetch event's video data **/
    [types.FETCH_EVENT_REQUEST]({ state, commit }, id) {
        return getEventData(id)
            .then(helper.errorResponseCheck)
            .then(response => response.json())
            .then(response => {
                const { attributes, id, nextEventToken, prevEventToken } = response.data;
                const { name, urls: { hd, pano }, thumbnails } = attributes;

                const eventData = {
                    id: id,
                    type: 'hd',
                    attributes: {
                        name,
                        urls: {
                            hd,
                            pano,
                            thumbnail: thumbnails.hd
                        }
                    }
                };

                const streams = [eventData];

                const additionalEvents = { nextEventToken, prevEventToken };
                const eventId = id;

                const highlights = attributes.highlights.map(item => {
                    const { id, name, type, thumbnail, url } = item;

                    return {
                        id,
                        type,
                        attributes: {
                            name,
                            urls: {
                                highlight: url,
                                thumbnail
                            },
                            eventId
                        }
                    }
                });

                const highlightIndex = highlights
                    .findIndex(item => state.video.streams.data.some(i => item.id === i.id));

                if (highlightIndex !== -1) {
                    highlights.splice(highlightIndex, 1);
                }

                streams.push(...highlights);

                commit(types.ADD_STREAMS, streams);

                return [eventData, additionalEvents];
            })
    },
};

const mutations = {
    ...linksMutations,

    [types.MAIN_FETCH_SUCCESS](state, payload) {
        state.video.mainPlaybackData = {
            data: payload,
            error: false,
            errorMessage: ''
        };
    },
    [types.MAIN_FETCH_FAILURE](state, payload) {
        state.video.mainPlaybackData = {
            error: true,
            errorMessage: payload
        };
    },
    [types.ADD_STREAMS](state, payload) {
        state.video.streams = {
            data: [
                ...state.video.streams.data,
                ...payload
            ]
        };
    },
    [types.ADD_THUMBNAIL](state, payload) {
        const stream = state.video.streams.data.find(item => item.id === payload.id);

        if (!stream || !stream.attributes) {
            return;
        }

        Vue.set(stream.attributes.urls, 'thumbnail', payload.thumbnail);
    },
    [types.SET_EVENT_DATA](state, payload) {
        const { logo, name, startDateTime, endDateTime } = payload;
        state.video.eventData = {
            logo,
            name,
            startDateTime,
            endDateTime
        }
    },
    [types.SET_ADDITIONAL_EVENT_TOKENS](state, payload) {
        state.nextEventToken = payload.nextEventToken;
        state.prevEventToken = payload.prevEventToken;
    },
    [types.INIT_VIDEO_DATA](state) {
        state.video = initialVideoData();
    },
    [types.SET_URL_HASH_DATA](state, payload) {
        state.urlHashData = payload;
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
